import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PrintTicket from '../components/PrintTicket';
import { 
  getRmaJobById, 
  updateRmaJobStatus, 
  updateRmaJobDiagnostics, 
  addRmaJobNote, 
  addRmaPart, 
  removeRmaPart,
  addRmaJobImage,
  removeRmaJobImage
} from '../utils/rmaDb';
import type { RmaJob, RmaStatus, RmaPriority, JobNote } from '../utils/rmaDb';
import {
  ArrowLeft,
  Printer,
  Edit3,
  Save,
  Plus,
  Trash2,
  User,
  Mail,
  Phone,
  Activity,
  FileText,
  Lock,
  Globe,
  Wrench,
  DollarSign,
  Sparkles,
  Camera,
  Upload
} from 'lucide-react';
import toast from 'react-hot-toast';

const RmaJobDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [job, setJob] = useState<RmaJob | null>(null);
  
  // Edit diagnostics state
  const [isEditingDiagnostics, setIsEditingDiagnostics] = useState(false);
  const [diagnosticsText, setDiagnosticsText] = useState('');
  
  // Add Note state
  const [newNoteText, setNewNoteText] = useState('');
  const [noteType, setNoteType] = useState<JobNote['type']>('tech'); // Default to technician internal log
  
  // Add Part state
  const [partName, setPartName] = useState('');
  const [partCost, setPartCost] = useState(0);
  const [partQty, setPartQty] = useState(1);
  const [isAddingPart, setIsAddingPart] = useState(false);
  
  // Status update modal state
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [targetStatus, setTargetStatus] = useState<RmaStatus | null>(null);
  const [statusComment, setStatusComment] = useState('');
  
  // Print Modal visibility
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Camera & Lightbox States
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Start Camera Stream
  const startCamera = async () => {
    try {
      setIsCameraOpen(true);
      setTimeout(async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: 'environment' }
          });
          setCameraStream(stream);
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
        } catch (e) {
          console.error('Camera stream access failed:', e);
          toast.error('Could not access camera. Please check permissions.');
          setIsCameraOpen(false);
        }
      }, 300);
    } catch (err) {
      console.error('Failed to open camera:', err);
      toast.error('Webcam initialization failed.');
      setIsCameraOpen(false);
    }
  };

  // Close Camera & Stop Streams
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraOpen(false);
  };

  // Capture Image Snapshot
  const capturePhoto = () => {
    if (!videoRef.current || !job) return;

    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        
        // Save to Local DB
        const updated = addRmaJobImage(job.id, dataUrl);
        if (updated) {
          setJob(updated);
          toast.success('Camera snapshot uploaded successfully!');
        } else {
          toast.error('Failed to save snapshot');
        }
      }
      
      stopCamera();
    } catch (err) {
      console.error('Photo capture failed:', err);
      toast.error('Could not capture frame.');
      stopCamera();
    }
  };

  // File Upload base64 Conversion
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!job || !e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are supported');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const updated = addRmaJobImage(job.id, reader.result);
        if (updated) {
          setJob(updated);
          toast.success('Diagnostic photo attached!');
        } else {
          toast.error('Failed to attach photo');
        }
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Delete Attached Image
  const handleDeleteImage = (index: number) => {
    if (!job) return;
    if (window.confirm('Are you sure you want to remove this attached image?')) {
      const updated = removeRmaJobImage(job.id, index);
      if (updated) {
        setJob(updated);
        toast.success('Diagnostic photo removed');
      } else {
        toast.error('Failed to remove photo');
      }
    }
  };

  useEffect(() => {
    loadJobDetails();
  }, [id]);

  useEffect(() => {
    // If URL has ?print=true, open print modal directly
    if (job && searchParams.get('print') === 'true') {
      setShowPrintModal(true);
    }
  }, [job, searchParams]);

  const loadJobDetails = () => {
    if (id) {
      const data = getRmaJobById(id);
      if (data) {
        setJob(data);
        setDiagnosticsText(data.diagnostics || '');
      } else {
        toast.error('RMA Job not found');
        navigate('/rma-jobs');
      }
    }
  };

  const handleSaveDiagnostics = () => {
    if (!job) return;
    const updated = updateRmaJobDiagnostics(job.id, diagnosticsText, 'Lead Technician');
    if (updated) {
      setJob(updated);
      setIsEditingDiagnostics(false);
      toast.success('Diagnostic findings updated');
    } else {
      toast.error('Failed to update diagnostics');
    }
  };

  const handleAddNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!job || !newNoteText.trim()) return;
    
    const updated = addRmaJobNote(job.id, newNoteText.trim(), 'Lead Technician', noteType);
    if (updated) {
      setJob(updated);
      setNewNoteText('');
      toast.success('Timeline note added');
    } else {
      toast.error('Failed to add note');
    }
  };

  const handleAddPart = (e: React.FormEvent) => {
    e.preventDefault();
    if (!job || !partName.trim() || partCost < 0 || partQty <= 0) return;

    const updated = addRmaPart(job.id, {
      name: partName.trim(),
      cost: Number(partCost),
      qty: Number(partQty)
    });

    if (updated) {
      setJob(updated);
      setPartName('');
      setPartCost(0);
      setPartQty(1);
      setIsAddingPart(false);
      toast.success('Part added and billing updated');
    } else {
      toast.error('Failed to add part');
    }
  };

  const handleRemovePart = (partId: string) => {
    if (!job) return;
    const updated = removeRmaPart(job.id, partId);
    if (updated) {
      setJob(updated);
      toast.success('Part removed and billing recalculated');
    } else {
      toast.error('Failed to remove part');
    }
  };

  const triggerStatusTransition = (status: RmaStatus) => {
    setTargetStatus(status);
    setStatusComment('');
    setIsUpdatingStatus(true);
  };

  const handleConfirmStatusChange = () => {
    if (!job || !targetStatus) return;
    const updated = updateRmaJobStatus(job.id, targetStatus, 'Lead Technician', statusComment.trim() || undefined);
    if (updated) {
      setJob(updated);
      setIsUpdatingStatus(false);
      setTargetStatus(null);
      setStatusComment('');
      toast.success(`Workflow advanced to ${targetStatus}`);
    } else {
      toast.error('Failed to transition status');
    }
  };

  if (!job) {
    return <div className="p-8 text-center text-slate-500">Loading repair records...</div>;
  }

  // Pre-calculate billing balances
  const partsSubtotal = job.parts.reduce((acc, p) => acc + (p.cost * p.qty), 0);
  const benchFee = job.parts.length > 0 ? 80 : 0; // Bench fee applies when parts are invoiced
  const estimatedCost = job.estimatedCost;
  const billingTotal = job.actualCost || (partsSubtotal + benchFee) || estimatedCost;
  const balanceDue = billingTotal - job.advancePayment;

  // Stepper status definitions
  const statusSteps: RmaStatus[] = ['Intake', 'Diagnostics', 'Awaiting Parts', 'Repairing', 'Testing', 'Ready', 'Delivered'];
  const currentStepIdx = statusSteps.indexOf(job.status);

  // Helper for priorities
  const getPriorityColor = (priority: RmaPriority) => {
    switch (priority) {
      case 'Urgent': return 'bg-rose-500 text-white ring-rose-500/20';
      case 'High': return 'bg-orange-500 text-white ring-orange-500/20';
      case 'Medium': return 'bg-blue-500 text-white ring-blue-500/20';
      default: return 'bg-slate-500 text-white ring-slate-500/20';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full text-slate-800">
      <div className="flex flex-1 w-full">
        <Sidebar />
        
        <main className="flex-1 p-6 md:p-8 w-full max-h-screen overflow-y-auto">
          <div className="w-full space-y-6">
            
            {/* TOP BAR / GLASS HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/rma-jobs')}
                  className="p-2.5 hover:bg-slate-100 border border-slate-200 rounded-xl text-slate-500 transition-colors"
                >
                  <ArrowLeft className="h-4.5 w-4.5" />
                </button>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-slate-900">{job.id}</h2>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${getPriorityColor(job.priority)}`}>
                      {job.priority} Priority
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Repair order for <span className="font-semibold text-slate-600">{job.customerName}</span> ({job.deviceBrand} {job.deviceModel})
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowPrintModal(true)}
                  className="inline-flex items-center gap-1.5 px-4.5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  Print Work Ticket
                </button>
              </div>
            </div>

            {/* INTERACTIVE WORKFLOW STEPPER */}
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
              <h3 className="text-xs font-bold uppercase text-slate-400 tracking-wider mb-5">Workflow & Status Tracker</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
                {statusSteps.map((status, idx) => {
                  const isCurrent = status === job.status;
                  const isCompleted = idx < currentStepIdx;
                  
                  return (
                    <button
                      key={status}
                      onClick={() => triggerStatusTransition(status)}
                      className={`relative p-3.5 rounded-xl border text-center transition-all flex flex-col items-center justify-center gap-1.5 hover:shadow-md ${
                        isCurrent 
                          ? 'border-blue-600 bg-blue-50/50 text-blue-900 font-bold' 
                          : isCompleted 
                            ? 'border-emerald-200 bg-emerald-50/20 text-emerald-800' 
                            : 'border-slate-150 bg-slate-50/50 text-slate-500'
                      }`}
                    >
                      {/* Check or Number Badge */}
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        isCurrent 
                          ? 'bg-blue-600 text-white' 
                          : isCompleted 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-slate-200 text-slate-500'
                      }`}>
                        {isCompleted ? '✓' : idx + 1}
                      </span>
                      <span className="text-[11px] uppercase tracking-wide leading-none">{status}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* SPLIT COLUMN DETAILS & TIMELINE */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* LEFT TWO COLUMNS (Specs, Diagnostics, Parts Billing) */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* 1. Device Specifications Card */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-50">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-5 w-5 text-indigo-500" />
                      <h3 className="font-bold text-slate-800">Device Specifications</h3>
                    </div>
                    <span className="text-xs text-slate-400 font-medium">Recorded in system</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                    <div className="space-y-3">
                      <div>
                        <span className="text-slate-400 uppercase font-semibold text-[10px] tracking-wide block">Device Category</span>
                        <span className="text-sm font-semibold text-slate-800 mt-0.5 block">{job.deviceCategory}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase font-semibold text-[10px] tracking-wide block">Brand / Model</span>
                        <span className="text-sm font-semibold text-slate-800 mt-0.5 block">{job.deviceBrand} - {job.deviceModel}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase font-semibold text-[10px] tracking-wide block">Serial / IMEI Code</span>
                        <span className="text-sm font-mono text-slate-800 mt-0.5 block">{job.serialNumber}</span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <span className="text-slate-400 uppercase font-semibold text-[10px] tracking-wide block">Assigned Technician</span>
                        <span className="text-sm font-semibold text-slate-800 mt-0.5 block">{job.assignedTo}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase font-semibold text-[10px] tracking-wide block">Date Logged Intake</span>
                        <span className="text-sm font-semibold text-slate-800 mt-0.5 block">{new Date(job.createdAt).toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase font-semibold text-[10px] tracking-wide block">Last System Activity</span>
                        <span className="text-sm font-semibold text-slate-850 mt-0.5 block">{new Date(job.updatedAt).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mt-2 text-xs">
                    <span className="text-slate-500 uppercase font-bold text-[10px] block mb-1">Issue Reported by Client:</span>
                    <p className="text-slate-700 italic leading-relaxed">"{job.reportedProblem}"</p>
                  </div>
                </div>

                {/* 2. Technical Diagnostics Log Editor */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-50">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-amber-500" />
                      <h3 className="font-bold text-slate-800">Technician Diagnostic Finding Log</h3>
                    </div>
                    {!isEditingDiagnostics ? (
                      <button
                        onClick={() => setIsEditingDiagnostics(true)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded-xl hover:bg-slate-50 text-xs font-semibold text-blue-600 transition-colors"
                      >
                        <Edit3 className="h-3.5 w-3.5" />
                        Edit Diagnostics
                      </button>
                    ) : (
                      <div className="flex gap-1.5">
                        <button
                          onClick={handleSaveDiagnostics}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-colors"
                        >
                          <Save className="h-3.5 w-3.5" />
                          Save Diagnostics
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingDiagnostics(false);
                            setDiagnosticsText(job.diagnostics || '');
                          }}
                          className="px-3 py-1.5 border border-slate-200 rounded-xl hover:bg-slate-50 text-xs font-semibold text-slate-650"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  {!isEditingDiagnostics ? (
                    <div className="text-xs leading-relaxed text-slate-700">
                      {job.diagnostics ? (
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 font-mono whitespace-pre-wrap">
                          {job.diagnostics}
                        </div>
                      ) : (
                        <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl text-slate-400 italic">
                          No technical diagnostic details entered yet. Click "Edit Diagnostics" to document root cause findings.
                        </div>
                      )}
                    </div>
                  ) : (
                    <textarea
                      rows={4}
                      value={diagnosticsText}
                      onChange={(e) => setDiagnosticsText(e.target.value)}
                      placeholder="Specify hardware board measurements, visual damages, components to replace..."
                      className="w-full p-3 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
                    />
                  )}
                </div>

                {/* 2.5. Device Condition & Diagnostic Media Card */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-50">
                    <div className="flex items-center gap-2">
                      <Camera className="h-5 w-5 text-blue-500" />
                      <h3 className="font-bold text-slate-800">Device Condition & Diagnostic Photos</h3>
                    </div>
                    <span className="text-xs text-slate-400 font-medium">Capture or upload media</span>
                  </div>

                  {/* Image Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {(job.images || []).map((img, idx) => (
                      <div key={idx} className="relative group rounded-xl overflow-hidden border border-slate-150 aspect-video bg-slate-50">
                        <img 
                          src={img} 
                          alt={`Intake status ${idx + 1}`} 
                          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-200"
                          onClick={() => setLightboxImage(img)}
                        />
                        {/* Hover Overlay Actions */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => setLightboxImage(img)}
                            className="p-1.5 bg-white/90 hover:bg-white text-slate-800 rounded-lg text-xs font-bold transition-colors"
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleDeleteImage(idx)}
                            className="p-1.5 bg-rose-600/90 hover:bg-rose-600 text-white rounded-lg text-xs font-bold transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Action Upload buttons inline as placeholders */}
                    <div className="border border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center p-3 text-center hover:bg-slate-50 transition-colors aspect-video">
                      <label className="cursor-pointer flex flex-col items-center justify-center w-full h-full text-blue-600 hover:text-blue-750">
                        <Upload className="h-5 w-5 mb-1 text-slate-450" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Upload File</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                    </div>

                    <button
                      onClick={startCamera}
                      className="border border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center p-3 text-center hover:bg-slate-50 text-blue-605 hover:text-blue-750 transition-colors aspect-video"
                    >
                      <Camera className="h-5 w-5 mb-1 text-slate-450" />
                      <span className="text-[10px] font-bold uppercase tracking-wider">Webcam Capture</span>
                    </button>
                  </div>

                  {(job.images || []).length === 0 && (
                    <div className="text-center py-4 text-slate-400 text-xs italic">
                      No diagnostic photos attached. Upload files or take a webcam snapshot of the device drop-off state.
                    </div>
                  )}
                </div>

                {/* 3. Parts & Service Costs billing */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-50">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-emerald-500" />
                      <h3 className="font-bold text-slate-800">Parts Used & Invoice Costs Billing</h3>
                    </div>
                    
                    {!isAddingPart && (
                      <button
                        onClick={() => setIsAddingPart(true)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-semibold transition-colors"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Link Billable Part
                      </button>
                    )}
                  </div>

                  {/* Add Part Form */}
                  {isAddingPart && (
                    <form onSubmit={handleAddPart} className="bg-slate-50 p-4 rounded-xl border border-slate-150 space-y-3.5">
                      <h4 className="text-xs font-bold text-slate-700 uppercase">Add Parts Ledger Details</h4>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-1">
                          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Part Name</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g. OLED Display Panel"
                            value={partName}
                            onChange={(e) => setPartName(e.target.value)}
                            className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Cost ($)</label>
                          <input
                            type="number"
                            required
                            min={0}
                            placeholder="Cost"
                            value={partCost}
                            onChange={(e) => setPartCost(Number(e.target.value))}
                            className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Quantity</label>
                          <input
                            type="number"
                            required
                            min={1}
                            placeholder="Qty"
                            value={partQty}
                            onChange={(e) => setPartQty(Number(e.target.value))}
                            className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 text-xs pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingPart(false);
                            setPartName('');
                            setPartCost(0);
                            setPartQty(1);
                          }}
                          className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-100 text-slate-650"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                        >
                          Add to Ticket
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Parts List */}
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-slate-100 font-bold text-slate-400 text-[10px] uppercase">
                        <th className="py-2.5">Linked Part</th>
                        <th className="py-2.5 text-right w-24">Unit Cost</th>
                        <th className="py-2.5 text-center w-16">Qty</th>
                        <th className="py-2.5 text-right w-24">Subtotal</th>
                        <th className="py-2.5 text-center w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {/* Bench Labor fee */}
                      <tr>
                        <td className="py-3 font-medium text-slate-700">Labor Bench Diagnostics & Clean Fee</td>
                        <td className="py-3 text-right text-slate-600">${benchFee.toFixed(2)}</td>
                        <td className="py-3 text-center text-slate-600">1</td>
                        <td className="py-3 text-right text-slate-700 font-bold">${benchFee.toFixed(2)}</td>
                        <td className="py-3"></td>
                      </tr>
                      
                      {job.parts.map((part) => (
                        <tr key={part.id} className="hover:bg-slate-50/50">
                          <td className="py-3 text-slate-700 font-semibold">{part.name}</td>
                          <td className="py-3 text-right text-slate-600">${part.cost.toFixed(2)}</td>
                          <td className="py-3 text-center text-slate-600">{part.qty}</td>
                          <td className="py-3 text-right text-slate-700 font-bold">${(part.cost * part.qty).toFixed(2)}</td>
                          <td className="py-3 text-center">
                            <button
                              onClick={() => handleRemovePart(part.id)}
                              className="text-slate-350 hover:text-rose-600 p-1 rounded transition-colors"
                              title="Delete Item"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}

                      {job.parts.length === 0 && (
                        <tr>
                          <td colSpan={5} className="py-3 text-center text-slate-400 italic">
                            No custom parts linked to this ticket yet. Click "Link Billable Part" to list materials used.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Totals Summary */}
                  <div className="bg-slate-50 p-4.5 rounded-xl border border-slate-100 flex flex-wrap gap-4 justify-between items-center text-xs">
                    <div className="flex gap-4">
                      <div>
                        <span className="text-slate-400 uppercase font-semibold text-[9px]">Est. Intake Quote</span>
                        <span className="text-sm font-bold text-slate-800 block mt-0.5">${estimatedCost}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 uppercase font-semibold text-[9px]">Deposit Paid</span>
                        <span className="text-sm font-bold text-slate-600 block mt-0.5">-${job.advancePayment}</span>
                      </div>
                    </div>
                    <div className="text-right border-l border-slate-200 pl-4">
                      <span className="text-slate-400 uppercase font-bold text-[10px] tracking-wider">Balance Due Payment</span>
                      <span className="text-lg font-black text-slate-900 block mt-0.5">
                        ${balanceDue.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {/* RIGHT ONE COLUMN (Customer Profile, Notes Timeline) */}
              <div className="space-y-6">
                
                {/* 1. Customer Card */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                  <div className="flex items-center gap-2 pb-3 border-b border-slate-50">
                    <User className="h-5 w-5 text-blue-500" />
                    <h3 className="font-bold text-slate-800">Customer Profile</h3>
                  </div>

                  <div className="space-y-3.5 text-xs">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 font-bold flex items-center justify-center">
                        {job.customerName.charAt(0)}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-850 text-sm">{job.customerName}</h4>
                        <span className="text-[10px] uppercase font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">RMA Client</span>
                      </div>
                    </div>

                    <div className="space-y-2 border-t border-slate-50 pt-3">
                      <a href={`tel:${job.customerPhone}`} className="flex items-center gap-2.5 text-slate-600 hover:text-blue-600 transition-colors">
                        <Phone className="h-4 w-4 text-slate-400" />
                        <span>{job.customerPhone}</span>
                      </a>
                      <a href={`mailto:${job.customerEmail}`} className="flex items-center gap-2.5 text-slate-600 hover:text-blue-600 transition-colors">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <span className="truncate">{job.customerEmail}</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* 2. Chronological Timeline of Job Notes */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between space-y-4 min-h-[450px]">
                  <div>
                    <div className="flex items-center justify-between pb-3 border-b border-slate-50 mb-4">
                      <div className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-indigo-500" />
                        <h3 className="font-bold text-slate-800">Repair Job Notes</h3>
                      </div>
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Chronological</span>
                    </div>

                    {/* Timeline List */}
                    <div className="space-y-4.5 max-h-[350px] overflow-y-auto pr-1">
                      {job.notes.map((note) => {
                        const isSystem = note.type === 'system';
                        const isTech = note.type === 'tech';
                        
                        return (
                          <div key={note.id} className="relative flex gap-3 text-xs">
                            {/* Icon column */}
                            <div className="flex flex-col items-center">
                              <span 
                                className={`w-6.5 h-6.5 rounded-full flex items-center justify-center border shadow-sm ${
                                  isSystem 
                                    ? 'bg-slate-150 border-slate-200 text-slate-500' 
                                    : isTech 
                                      ? 'bg-blue-50 border-blue-150 text-blue-600' 
                                      : 'bg-emerald-50 border-emerald-150 text-emerald-600'
                                }`}
                                title={isSystem ? 'System auto-log' : isTech ? 'Internal technician note' : 'Public client update'}
                              >
                                {isSystem ? (
                                  <FileText className="h-3 w-3" />
                                ) : isTech ? (
                                  <Lock className="h-3 w-3" />
                                ) : (
                                  <Globe className="h-3 w-3" />
                                )}
                              </span>
                              <div className="flex-1 w-0.5 bg-slate-100 mt-2"></div>
                            </div>
                            
                            {/* Content column */}
                            <div className="flex-1 min-w-0 bg-slate-50/50 border border-slate-100 p-3 rounded-xl">
                              <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                                <span className="font-bold text-slate-700">{note.author}</span>
                                <span className="text-[9px] text-slate-400">
                                  {new Date(note.timestamp).toLocaleDateString()} {new Date(note.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <p className="text-slate-650 leading-relaxed font-medium">{note.text}</p>
                              
                              <div className="flex justify-end mt-1.5">
                                <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-md uppercase border ${
                                  isSystem 
                                    ? 'bg-slate-100 text-slate-500 border-slate-200' 
                                    : isTech 
                                      ? 'bg-blue-100 text-blue-700 border-blue-200' 
                                      : 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                }`}>
                                  {note.type === 'tech' ? 'internal log' : note.type === 'system' ? 'system log' : 'shared update'}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Add Note Form */}
                  <form onSubmit={handleAddNote} className="border-t border-slate-100 pt-4 space-y-3">
                    <textarea
                      rows={2.5}
                      required
                      placeholder="Write a status update log, diagnostic detail, parts progress, or client note..."
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      className="w-full p-2.5 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50 hover:bg-white transition-colors"
                    />

                    <div className="flex justify-between items-center gap-2">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setNoteType('tech')}
                          className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all flex items-center gap-1 ${
                            noteType === 'tech'
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                          }`}
                        >
                          <Lock className="h-3 w-3" />
                          Internal Tech
                        </button>
                        <button
                          type="button"
                          onClick={() => setNoteType('client')}
                          className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all flex items-center gap-1 ${
                            noteType === 'client'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                          }`}
                        >
                          <Globe className="h-3 w-3" />
                          Shared Client
                        </button>
                      </div>

                      <button
                        type="submit"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-colors shadow-sm"
                      >
                        Add Note
                      </button>
                    </div>
                  </form>

                </div>

              </div>

            </div>

          </div>
        </main>
      </div>

      {/* CONFIRM STATUS TRANSITION DIALOG MODAL */}
      {isUpdatingStatus && targetStatus && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-slate-150 p-6 space-y-4">
            <div>
              <h3 className="text-base font-bold text-slate-900">Advance Workflow Status?</h3>
              <p className="text-xs text-slate-500 mt-1">
                You are transitioning ticket <span className="font-semibold">{job.id}</span> from <span className="font-bold text-slate-700">{job.status}</span> to <span className="font-bold text-blue-600">{targetStatus}</span>.
              </p>
            </div>

            <div>
              <label htmlFor="statusComment" className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                Technician Comments (Optional)
              </label>
              <textarea
                id="statusComment"
                rows={2}
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                placeholder="e.g. Ultrasonic bath cleaned logic board, screen replacing completed..."
                className="w-full p-2.5 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            <div className="flex justify-end gap-2 text-xs">
              <button
                onClick={() => {
                  setIsUpdatingStatus(false);
                  setTargetStatus(null);
                  setStatusComment('');
                }}
                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStatusChange}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow-sm"
              >
                Confirm Advance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PRINT WORK ORDER OVERLAY MODAL */}
      {showPrintModal && (
        <PrintTicket 
          job={job} 
          onClose={() => {
            setShowPrintModal(false);
            // strip query params
            navigate(`/rma-jobs/${job.id}`, { replace: true });
          }} 
        />
      )}

      {/* WEBCAM CAMERA MODAL OVERLAY */}
      {isCameraOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-2xl w-full max-w-lg shadow-2xl border border-slate-800 p-6 flex flex-col items-center gap-4 text-white">
            <div className="w-full flex justify-between items-center pb-2 border-b border-slate-800">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-350">Live Webcam Capture</h3>
              <button 
                onClick={stopCamera}
                className="text-slate-455 hover:text-white transition-colors font-bold text-xs"
              >
                ✕
              </button>
            </div>

            {/* Video Box */}
            <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden border border-slate-800 flex items-center justify-center">
              <video 
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              {!cameraStream && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-500 animate-pulse">
                  Requesting camera device access...
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex gap-3 text-xs pt-2">
              <button
                onClick={stopCamera}
                className="px-4 py-2 border border-slate-700 hover:bg-slate-800 rounded-xl font-semibold text-slate-300"
              >
                Cancel
              </button>
              <button
                onClick={capturePhoto}
                disabled={!cameraStream}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-md disabled:opacity-50 transition-colors"
              >
                <Camera className="h-4 w-4" />
                Capture Snapshot
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX PHOTO MODAL OVERLAY */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 p-2.5 rounded-full transition-colors font-bold text-lg"
            onClick={() => setLightboxImage(null)}
          >
            ✕
          </button>
          <div className="max-w-4xl max-h-[90vh]">
            <img 
              src={lightboxImage} 
              alt="Diagnostic media fullscreen" 
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl border border-white/5"
            />
            <p className="text-center text-xs text-slate-400 mt-3 font-semibold">Click anywhere to close full screen view</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default RmaJobDetail;
